// The customers screens.
//
// Every class used here (`btn`, `field`, `pill`, `card`, `chip`, `truncate`,
// `focusable`) comes from the shared design system — the verbatim copy of
// apps/fe/index.html's token block. Nothing in this file defines a colour.
// `design-system.test.ts` enforces that rather than trusting it.
//
// ⚠️ Stage A's "read-only" notice USED to live on the customer page, saying that
// the console's database login held no write permission at all. Stage B granted
// it, so that sentence became false the day the pause switch shipped — and a
// reassuring, false sentence about permissions is worse than none. It is
// replaced below by the controls it was apologising for.

import { useState } from "react";
import {
  type ApiError,
  type ConsoleApi,
  type CustomerDetail,
  type CustomerList,
  type CustomerSummary,
} from "./api.js";
import { AiCostsSection } from "./ai-costs.js";
import { IllustrationStyleSection } from "./profile-editor.js";
import { TabPanel, Tabs } from "./tabs.js";
import {
  ErrorState,
  fmtDate,
  InlineError,
  Labelled,
  Loading,
  Modal,
  Row,
  Section,
  statusPill,
  useAsync,
  useSubmitLatch,
} from "./ui.js";

// ── Customers ───────────────────────────────────────────────────────────────

export function CustomersScreen({
  api,
  onOpen,
}: {
  api: ConsoleApi;
  onOpen: (id: string) => void;
}) {
  // OFF by default — Omar, 2026-08-16: "Mark the test company internal so it
  // never pollutes your customer numbers."
  const [includeInternal, setIncludeInternal] = useState(false);
  const { state, retry } = useAsync<CustomerList>(
    () => api.customers(includeInternal),
    [includeInternal],
  );

  return (
    <div className="console-page">
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 14,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 17,
            fontWeight: 600,
            color: "var(--text-strong)",
          }}
        >
          Customers
        </h1>
        {state.t === "ok" && (
          <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            {state.data.customers.length}
            {state.data.internalHidden > 0 && (
              // Say what is hidden. A list that silently shows 3 of 4 is a small
              // lie, and this number is the one Omar makes decisions on.
              <> · {state.data.internalHidden} internal hidden</>
            )}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 12.5,
            color: "var(--text-muted)",
            cursor: "default",
          }}
        >
          <input
            type="checkbox"
            className="focusable"
            checked={includeInternal}
            onChange={(e) => setIncludeInternal(e.target.checked)}
          />
          Show internal / test accounts
        </label>
      </div>

      {state.t === "loading" && (
        <Loading label="Reading the customer registry…" />
      )}
      {state.t === "err" && <ErrorState err={state.err} onRetry={retry} />}
      {state.t === "ok" &&
        (state.data.customers.length === 0 ? (
          <EmptyCustomers hiddenCount={state.data.internalHidden} />
        ) : (
          <div className="console-table-wrap">
            <table className="console-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Identity</th>
                  <th>Contact</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {state.data.customers.map((c) => (
                  <CustomerRow key={c.id} c={c} onOpen={onOpen} />
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
}

function CustomerRow({
  c,
  onOpen,
}: {
  c: CustomerSummary;
  onOpen: (id: string) => void;
}) {
  return (
    <tr onClick={() => onOpen(c.id)} style={{ cursor: "default" }}>
      {/* data-label on EVERY cell: below 700px the table becomes stacked rows and
          the header is hidden, so without it a value appears with nothing saying
          what it is. design-system.test.ts checks this. */}
      <td data-label="Customer">
        <button
          className="focusable"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(c.id);
          }}
          style={{
            background: "none",
            border: 0,
            padding: 0,
            font: "inherit",
            color: "var(--accent-text)",
            fontWeight: 600,
            cursor: "default",
            textAlign: "left",
          }}
        >
          {c.displayName}
        </button>
        <div
          style={{
            fontSize: 11.5,
            color: "var(--text-faint)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {c.slug}
        </div>
      </td>
      <td data-label="Status">
        <span className={statusPill(c.status)}>{c.status}</span>
        {c.isInternal && (
          <span className="pill pending" style={{ marginLeft: 6 }}>
            internal
          </span>
        )}
      </td>
      <td data-label="Identity">
        {/* An honest three-state fact, not a blank. "No Organization" is the
            NORMAL and correct state for an individual customer under the two-door
            model (§2.9) — it is not a missing value. */}
        {c.auth0OrgId ? (
          <span className="pill accepted">Organization</span>
        ) : (
          <span
            className="pill pending"
            title="Individual account — no Auth0 Organization needed"
          >
            Individual
          </span>
        )}
      </td>
      <td data-label="Contact">
        <span className="truncate" style={{ display: "block", maxWidth: 200 }}>
          {c.contactEmail ?? "—"}
        </span>
      </td>
      <td data-label="Created">{fmtDate(c.createdAt)}</td>
    </tr>
  );
}

function EmptyCustomers({ hiddenCount }: { hiddenCount: number }) {
  return (
    <div className="card" style={{ padding: 24, maxWidth: 620 }}>
      <p
        style={{
          margin: "0 0 8px",
          fontSize: 14,
          fontWeight: 600,
          color: "var(--text-strong)",
        }}
      >
        No customers to show
      </p>
      {/* ⚠️ An empty list has TWO very different causes and they must not read the
          same. This screen is exactly where a silently no-op migration surfaced
          as "you have no customers" — see the 2026-08-17 finding. So the empty
          state names both possibilities instead of asserting one. */}
      <p
        style={{
          margin: "0 0 10px",
          fontSize: 13,
          lineHeight: 1.55,
          color: "var(--text-muted)",
        }}
      >
        {hiddenCount > 0 ? (
          <>
            All {hiddenCount} workspace{hiddenCount === 1 ? "" : "s"} in this
            environment are marked internal. Tick{" "}
            <em>Show internal / test accounts</em> to see them.
          </>
        ) : (
          <>
            Either this environment genuinely has no customers, or the registry
            backfill did not run. Those look identical from here, so check the
            second one: the migration prints{" "}
            <code style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
              N workspace(s) exist
            </code>{" "}
            when it applies.
          </>
        )}
      </p>
    </div>
  );
}

// ── Customer detail ─────────────────────────────────────────────────────────

export function CustomerDetailScreen({
  api,
  id,
  onBack,
}: {
  api: ConsoleApi;
  id: string;
  onBack: () => void;
}) {
  const { state, retry } = useAsync<CustomerDetail>(
    () => api.customer(id),
    [id],
  );

  return (
    <div className="console-page">
      <button
        className="btn ghost sm focusable"
        onClick={onBack}
        style={{ marginBottom: 12 }}
      >
        ← All customers
      </button>

      {state.t === "loading" && <Loading label="Reading this customer…" />}
      {state.t === "err" && <ErrorState err={state.err} onRetry={retry} />}
      {state.t === "ok" && (
        <CustomerBody api={api} d={state.data} onChanged={retry} />
      )}
    </div>
  );
}

/**
 * ── THE COMPANY PAGE, IN THREE TABS ────────────────────────────────────────
 *
 * Omar, 2026-09-08 (verbatim, and it is the whole specification):
 *
 *   "Replace the current long page with horizontal tabs, following the design
 *    pattern used in the customer Settings page …
 *      Overview      — Access, 'Course files are readable', Contact, and Identity
 *      Illustration  — The company's editable illustration-style settings
 *      AI costs      — Usage and cost reporting across the company's courses,
 *                      with access to each course's breakdown
 *    Preserve the existing functionality and editing permissions."
 *
 * ★ NOTHING WAS REMOVED IN THE REORGANISATION, which is the one risk a
 *   reshuffle like this carries. Two sections he did not name in the table are
 *   still here and still reachable:
 *
 *     · the three counts (Courses / People / Last activity) sit ABOVE the tab
 *       strip, as facts about the company rather than about one tab. That also
 *       keeps "Courses: 7" on screen while the AI costs tab lists courses,
 *       which is where the number is most useful;
 *     · "Dates" (Registered / Provisioned) is inside Overview, under Identity,
 *       where it belongs by subject.
 *
 *   Deleting either as a side effect of a layout change is the failure
 *   `feedback_do_not_remove_a_control_the_change_made_redundant` records — a
 *   reorganisation is not a licence to drop what the new shape has no obvious
 *   slot for.
 *
 * ★ AND EDITING PERMISSIONS ARE UNTOUCHED. Nothing here gates a tab. The
 *   illustration editor refuses a save the gateway would refuse, and the
 *   gateway refuses it because of a GRANT, not because of which tab was open
 *   (`feedback_verify_the_control_not_just_the_pipeline`).
 */
const CUSTOMER_TABS = [
  { id: "overview", label: "Overview" },
  // Omar, 2026-09-15: "a new tab where I can see also the Owner and members
  // with their role that have been added ... This way I can count how many
  // users have been added and the role they have." Second, next to Overview,
  // because it answers a question about the customer rather than about their
  // content or their bill.
  //
  // ⚠️ "Members", NOT "People" — which is what it was called for about ten
  //    minutes. The stat strip directly above the tabs already has a box
  //    labelled PEOPLE, so two different things on one screen would have
  //    carried one word, and a test asking for "People" found both. It is also
  //    the word the customer's own screen uses (Settings > Members), which is
  //    the screen Omar pointed at: "Similar to the existing Members section on
  //    the Settings page." One thing, one name
  //    (`feedback_a_readable_label_can_name_the_wrong_thing`).
  { id: "members", label: "Members" },
  { id: "illustration", label: "Illustration" },
  { id: "aiCosts", label: "AI costs" },
] as const;

type CustomerTabId = (typeof CUSTOMER_TABS)[number]["id"];

/**
 * Every person in a workspace, with the role they hold.
 *
 * ── Why this is READ-ONLY, unlike the customer's own Settings > Members ─────
 * The screen it is modelled on (`apps/fe/src/surface-members.jsx`) is where a
 * workspace ADMIN manages their own people, and every control there is backed
 * by a route that enforces the same rule server-side. Nothing equivalent exists
 * for staff, so a Change or Remove button here would be a control that cannot
 * work (`feedback_no_false_affordance_toggles`). Omar asked to SEE the list and
 * count it; that is exactly what this does.
 */
const MEMBER_ROLE_LABEL: Record<string, string> = {
  // ★ Lifted verbatim from `apps/fe/src/surface-members.jsx`'s MEMBER_ROLES, so
  //   staff and the customer read the same words for the same role. An unknown
  //   id falls through to itself rather than to a guess, because a readable
  //   label that names the wrong thing is worse than an opaque one
  //   (`feedback_a_readable_label_can_name_the_wrong_thing`).
  admin: "Admin",
  editor: "Editor",
  reviewer: "Reviewer",
  language_reviewer: "Language Reviewer",
};

function PeopleSection({ api, id }: { api: ConsoleApi; id: string }) {
  const { state, retry } = useAsync(() => api.workspaceMembers(id), [id]);
  if (state.t === "loading")
    return <Loading label="Reading this workspace's people…" />;
  if (state.t === "err") return <ErrorState err={state.err} onRetry={retry} />;
  const { contactEmail, contactHasAccount, members } = state.data;

  return (
    <>
      <Section
        title={`${members.length} ${members.length === 1 ? "person" : "people"}`}
      >
        {members.length === 0 ? (
          <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>
            Nobody has signed in to this workspace yet.
            {contactEmail ? ` It was registered for ${contactEmail}.` : ""}
          </p>
        ) : (
          <div className="console-table-wrap">
            <table className="console-table">
              <thead>
                <tr>
                  <th style={{ width: "42%" }}>Person</th>
                  <th>Access</th>
                  <th>Languages</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.email} data-static="">
                    <td data-label="Person">
                      <div
                        style={{
                          fontSize: 13.5,
                          fontWeight: 500,
                          color: "var(--text)",
                        }}
                      >
                        {m.displayName ?? m.email}
                        {m.isContact && (
                          // ★ The word Omar used. There is no owner COLUMN — this
                          //   is the address the workspace was registered for, so
                          //   the badge says which fact it is stating.
                          <span
                            className="pill"
                            style={{ marginLeft: 8 }}
                            title="The address this workspace was registered for"
                          >
                            Owner
                          </span>
                        )}
                      </div>
                      {m.displayName !== null && (
                        <div
                          style={{
                            fontSize: 11.5,
                            color: "var(--text-muted)",
                            marginTop: 2,
                          }}
                        >
                          {m.email}
                        </div>
                      )}
                    </td>
                    <td data-label="Access">
                      {MEMBER_ROLE_LABEL[m.role] ?? m.role}
                    </td>
                    <td data-label="Languages">
                      {m.assignedLangs.length > 0
                        ? m.assignedLangs.join(", ")
                        : "—"}
                    </td>
                    <td data-label="Status">
                      <span className={statusPill(m.status)}>{m.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* ★ THE STATE THAT WOULD OTHERWISE READ AS "no owner". A workspace can be
          registered before anyone signs in, and then nobody carries the badge —
          which is a fact about sign-ins, not about the contact being missing. */}
      {contactEmail !== null && !contactHasAccount && members.length > 0 && (
        <p
          style={{
            margin: "10px 0 0",
            fontSize: 12,
            lineHeight: 1.55,
            color: "var(--warning-text)",
          }}
        >
          This workspace was registered for <strong>{contactEmail}</strong>, who
          has not signed in — so no row above is marked Owner.
        </p>
      )}
    </>
  );
}

function CustomerBody({
  api,
  d,
  onChanged,
}: {
  api: ConsoleApi;
  d: CustomerDetail;
  onChanged: () => void;
}) {
  const c = d.customer;
  const [tab, setTab] = useState<CustomerTabId>("overview");

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 4,
          flexWrap: "wrap",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 19,
            fontWeight: 600,
            color: "var(--text-strong)",
          }}
        >
          {c.displayName}
        </h1>
        <span className={statusPill(c.status)}>{c.status}</span>
        {c.isInternal && <span className="pill pending">internal</span>}
      </div>
      <p
        style={{
          margin: "0 0 18px",
          fontSize: 12,
          color: "var(--text-faint)",
          fontFamily: "var(--font-mono)",
        }}
      >
        {c.slug}
      </p>

      {/* Above the tabs: facts about the company, true on whichever tab is open. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 10,
        }}
      >
        <Stat label="Courses" value={String(d.stats.courseCount)} />
        <Stat label="People" value={String(d.stats.userCount)} />
        <Stat label="Last activity" value={fmtDate(d.stats.lastActivity)} />
      </div>

      <Tabs
        tabs={CUSTOMER_TABS}
        current={tab}
        onSelect={setTab}
        idPrefix="customer"
      />

      {tab === "overview" && (
        <TabPanel id="overview" idPrefix="customer">
          <PauseCard api={api} c={c} onChanged={onChanged} />
          <ProtectionCard api={api} c={c} onChanged={onChanged} />

          <Section title="Contact">
            <Row label="Name" value={c.contactName ?? "—"} />
            <Row label="Email" value={c.contactEmail ?? "—"} />
            {/* "Phone" and "VAT number" were removed on Omar's instruction,
                2026-10-29. Nothing ever filled them: they always read "—". */}
          </Section>

          <Section title="Identity">
            <Row
              label="Sign-in model"
              value={
                d.auth0.provisioned
                  ? "Auth0 Organization (enterprise)"
                  : "Individual account (no Organization)"
              }
            />
            <Row
              label="Auth0 Organization id"
              value={d.auth0.organizationId ?? "— not provisioned"}
            />
            <Row label="Workspace id" value={c.orgId} mono />
          </Section>

          <Section title="Dates">
            <Row label="Registered" value={fmtDate(c.createdAt)} />
            <Row label="Provisioned" value={fmtDate(c.provisionedAt)} />
          </Section>
        </TabPanel>
      )}

      {tab === "members" && (
        <TabPanel id="members" idPrefix="customer">
          <PeopleSection api={api} id={c.id} />
        </TabPanel>
      )}

      {tab === "illustration" && (
        <TabPanel id="illustration" idPrefix="customer">
          <IllustrationStyleSection
            api={api}
            id={c.id}
            companyName={c.displayName}
          />
        </TabPanel>
      )}

      {tab === "aiCosts" && (
        <TabPanel id="aiCosts" idPrefix="customer">
          <AiCostsSection api={api} id={c.id} />
        </TabPanel>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card" style={{ padding: "12px 14px" }}>
      <div
        style={{
          fontSize: 10.5,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--text-faint)",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        className="console-num"
        style={{
          fontSize: 21,
          fontWeight: 600,
          color: "var(--text-strong)",
          marginTop: 3,
          textAlign: "left",
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ── Pause / Resume ──────────────────────────────────────────────────────────

/**
 * ★ THE PAUSE. Omar, 2026-08-21: *"I need to be able to pause their access
 * through the console."*
 *
 * Stage B ships with no trial clock, so this is the containment mechanism that
 * replaces it — and it is a big lever: one click reaches every person in that
 * workspace at once, on their very next action, with no warning to them. So the
 * card states what happens in the plainest words available BEFORE the button,
 * not in a confirmation after it.
 *
 * The two things people fear are answered explicitly, because "paused" sounds
 * like it might mean "deleted": nothing is deleted, and resuming puts it back.
 */
/**
 * ★ CONTENT PROTECTION — Omar's switch, 2026-09-01.
 *
 * Written for the person using it, who is not a developer: it says what the
 * customer receives, not how it is done. No mention of formats, scrambling or
 * encryption — and deliberately no promise of "impossible to read", because
 * that is not what this is (see packages/scorm-packager/src/protect.ts).
 *
 * The two things worth knowing before touching it are stated BEFORE the button:
 * it only affects packages built from now on, and turning it off is how you get
 * a package you can open and inspect.
 */
function ProtectionCard({
  api,
  c,
  onChanged,
}: {
  api: ConsoleApi;
  c: CustomerSummary;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const on = c.protectExports;

  return (
    <>
      <div className="card" style={{ padding: "12px 14px", marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--text-strong)",
              }}
            >
              {on ? "Course files are protected" : "Course files are readable"}
            </div>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 12.5,
                lineHeight: 1.55,
                color: "var(--text-muted)",
              }}
            >
              {on ? (
                <>
                  Packages {c.displayName} downloads cannot be opened or edited
                  in a text editor, and a package that has been altered refuses
                  to run. Every file carries the Dynamo Srl copyright and an
                  identifier that traces the copy back to them.
                </>
              ) : (
                <>
                  Packages {c.displayName} downloads can be opened and edited by
                  anyone who knows where to look. They still carry the Dynamo
                  Srl copyright. Turn this on to make the course files
                  unreadable from their next download onwards.
                </>
              )}
            </p>
            <p
              style={{
                margin: "6px 0 0",
                fontSize: 12,
                lineHeight: 1.5,
                color: "var(--text-muted)",
              }}
            >
              This changes packages built from now on. Anything already
              downloaded stays as it is.
            </p>
          </div>
          <button
            className={on ? "btn focusable" : "btn primary focusable"}
            onClick={() => setOpen(true)}
            data-testid={on ? "unprotect-open" : "protect-open"}
          >
            {on ? "Make readable" : "Protect exports"}
          </button>
        </div>
      </div>

      {open && (
        <ProtectionDialog
          api={api}
          c={c}
          on={on}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            onChanged();
          }}
        />
      )}
    </>
  );
}

function ProtectionDialog({
  api,
  c,
  on,
  onClose,
  onDone,
}: {
  api: ConsoleApi;
  c: CustomerSummary;
  on: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<ApiError | null>(null);
  const [already, setAlready] = useState(false);
  const { run, busy } = useSubmitLatch();
  const blocked = reason.trim().length < 3;

  const submit = () => {
    if (blocked) return;
    setErr(null);
    run(
      () => api.setExportProtection(c.id, !on, reason.trim()),
      (result) => {
        // Same reasoning as the pause dialog: somebody else may have acted on
        // this customer while this was open, and "done" would hide that.
        if (result.alreadyInThatState) setAlready(true);
        else onDone();
      },
      (e) => setErr(e),
    );
  };

  if (already) {
    return (
      <Modal
        title="Already done"
        busy={false}
        onClose={onDone}
        footer={
          <button className="btn primary focusable" onClick={onDone}>
            Close
          </button>
        }
      >
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          {c.displayName} was already set that way. Somebody else may have
          changed it while this was open.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title={
        on
          ? "Make this customer's exports readable"
          : "Protect this customer's exports"
      }
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <button className="btn focusable" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn primary focusable"
            onClick={submit}
            disabled={blocked || busy}
            data-testid="protection-confirm"
          >
            {on ? "Make readable" : "Protect exports"}
          </button>
        </>
      }
    >
      <p style={{ margin: "0 0 10px", fontSize: 13, lineHeight: 1.6 }}>
        {on ? (
          <>
            From now on, packages {c.displayName} downloads can be opened and
            read in a text editor. Use this when you need to inspect one of
            their courses yourself.
          </>
        ) : (
          <>
            From now on, packages {c.displayName} downloads cannot be opened or
            edited in a text editor, and an altered package refuses to run.
          </>
        )}
      </p>
      <p
        style={{
          margin: "0 0 10px",
          fontSize: 12.5,
          lineHeight: 1.55,
          color: "var(--text-muted)",
        }}
      >
        Packages they have already downloaded do not change.
      </p>
      <label style={{ display: "block", fontSize: 12.5, marginBottom: 6 }}>
        Why are you doing this? It is recorded and cannot be edited later.
      </label>
      <input
        className="focusable"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={on ? "Inspecting a reported issue" : "Standard protection"}
        style={{ width: "100%" }}
        data-testid="protection-reason"
      />
      {err && <InlineError err={err} />}
    </Modal>
  );
}

function PauseCard({
  api,
  c,
  onChanged,
}: {
  api: ConsoleApi;
  c: CustomerSummary;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const paused = c.status === "suspended";

  return (
    <>
      <div className="card" style={{ padding: "12px 14px", marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--text-strong)",
              }}
            >
              {paused ? "This customer is paused" : "Access"}
            </div>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 12.5,
                lineHeight: 1.55,
                color: "var(--text-muted)",
              }}
            >
              {paused ? (
                <>
                  Nobody at {c.displayName} can use Dynamo. Their courses,
                  drafts and files are all still there, exactly as they left
                  them.
                </>
              ) : (
                <>
                  Everyone at {c.displayName} can use Dynamo normally. Pausing
                  stops all of them at once, from their next action — nothing is
                  deleted and resuming puts it back.
                </>
              )}
            </p>
          </div>
          <button
            className={
              paused ? "btn primary focusable" : "btn danger focusable"
            }
            onClick={() => setOpen(true)}
            data-testid={paused ? "resume-open" : "pause-open"}
          >
            {paused ? "Resume access" : "Pause access"}
          </button>
        </div>
      </div>

      {open && (
        <PauseDialog
          api={api}
          c={c}
          paused={paused}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            onChanged();
          }}
        />
      )}
    </>
  );
}

function PauseDialog({
  api,
  c,
  paused,
  onClose,
  onDone,
}: {
  api: ConsoleApi;
  c: CustomerSummary;
  paused: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<ApiError | null>(null);
  const [already, setAlready] = useState(false);
  const { run, busy } = useSubmitLatch();
  const blocked = reason.trim().length < 3;

  const submit = () => {
    if (blocked) return;
    setErr(null);
    run(
      () =>
        paused
          ? api.resumeCustomer(c.id, reason.trim())
          : api.suspendCustomer(c.id, reason.trim()),
      (result) => {
        // ⚠️ Reported, not swallowed. "Already in that state" means somebody
        // else acted on this customer while this dialog was open, and an
        // operator who is told "done" learns nothing about the other person.
        if (result.alreadyInThatState) setAlready(true);
        else onDone();
      },
      (e) => setErr(e),
    );
  };

  if (already) {
    return (
      <Modal
        title="Already done"
        busy={false}
        onClose={onDone}
        footer={
          <button className="btn primary focusable" onClick={onDone}>
            Close
          </button>
        }
      >
        <p
          style={{
            margin: 0,
            fontSize: 13,
            lineHeight: 1.6,
            color: "var(--text)",
          }}
        >
          {c.displayName} was already {paused ? "active" : "paused"} when this
          went through, so nothing changed. Somebody else may be working on this
          customer at the same time — the page will refresh when you close this.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title={paused ? `Resume ${c.displayName}` : `Pause ${c.displayName}`}
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <button
            className="btn ghost focusable"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className={
              paused ? "btn primary focusable" : "btn danger focusable"
            }
            onClick={submit}
            disabled={busy || blocked}
            data-testid="pause-submit"
          >
            {busy ? "Working…" : paused ? "Resume access" : "Pause access"}
          </button>
        </>
      }
    >
      {err && <InlineError err={err} />}

      <div className="card" style={{ padding: "10px 12px", marginBottom: 12 }}>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            lineHeight: 1.6,
            color: "var(--text)",
          }}
        >
          {paused ? (
            <>
              Everyone at {c.displayName} will be able to use Dynamo again,
              starting with their very next action. They do not need to sign in
              again.
            </>
          ) : (
            <>
              <strong>Everyone</strong> at {c.displayName} stops being able to
              use Dynamo, starting with their very next action — mid-edit, with
              no warning from us. They will see a message telling them to
              contact Dynamo.
            </>
          )}
        </p>
        {!paused && (
          <p
            style={{
              margin: "8px 0 0",
              fontSize: 12.5,
              lineHeight: 1.55,
              color: "var(--text-muted)",
            }}
          >
            Nothing is deleted. No course, draft, file or person is removed, and
            resuming puts it back exactly as it was.
          </p>
        )}
      </div>

      <Labelled
        label={paused ? "Why you are resuming" : "Why you are pausing"}
        hint={
          blocked ? (
            <span style={{ color: "var(--error-text)" }}>
              Required — it is recorded in the audit trail and cannot be edited
              later.
            </span>
          ) : (
            "Recorded in the audit trail. It is for you in six months."
          )
        }
      >
        <textarea
          className="field focusable"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          style={{ width: "100%" }}
          data-testid="pause-reason"
        />
      </Labelled>
    </Modal>
  );
}

// ── Not built yet ───────────────────────────────────────────────────────────

/**
 * An honest empty state, per §5 D6 and `feedback_honest_gates_over_standins`.
 * Not a mock list, not a disabled button — a sentence saying what will be here
 * and what has to happen first.
 *
 * ⚠️ Rewritten 2026-08-21, and the reason is worth keeping. The previous copy
 * said the audit screen would land "with Stage B's first write action — there is
 * nothing but sign-ins to show until something can be changed". Stage B then
 * shipped four write actions, and the sentence quietly became false: the trail
 * now fills up every time Omar approves or pauses somebody, while this screen
 * still told him there was nothing in it. An honest placeholder has to be
 * re-read whenever the thing it is waiting for arrives, or it turns into a
 * confident lie (`feedback_a_test_named_after_the_gap_expires`).
 */
export function NotBuiltYet({ surface }: { surface: string }) {
  const copy: Record<string, { title: string; body: React.ReactNode }> = {
    activity: {
      title: "Activity",
      body: (
        <>
          Every approval, rejection, pause and resume is{" "}
          <strong>already being recorded</strong>, with the reason you typed —
          that started with Stage B. What is missing is this screen and the
          route behind it, so the trail exists and cannot yet be read back here.
          Until then it is only visible in the database.
        </>
      ),
    },
  };
  const c = copy[surface] ?? { title: surface, body: "Not built yet." };
  return (
    <div className="console-page">
      <h1
        style={{
          margin: "0 0 10px",
          fontSize: 17,
          fontWeight: 600,
          color: "var(--text-strong)",
        }}
      >
        {c.title}
      </h1>
      <div className="card" style={{ padding: 20, maxWidth: 620 }}>
        <span
          className="pill pending"
          style={{ marginBottom: 10, display: "inline-flex" }}
        >
          Not built yet
        </span>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            lineHeight: 1.6,
            color: "var(--text-muted)",
          }}
        >
          {c.body}
        </p>
      </div>
    </div>
  );
}
